// PHA-1168: the view builder. A clinic admin describes a view in plain English;
// the AI turns it into a view definition (never code, never SQL, never sees
// patient rows); the preview runs it on the clinic's real data (first 50 rows);
// Save compiles it into a fixed query. Opening the Patient List / Billing Report
// afterwards never calls the AI.
//
// PHA-1168 Reports: the same builder designs reports (kind="report"). A report's
// surface is its dataset; it has no "default", no hidden-rows confirmation, and
// lives under /reports with the Reports layout instead of Clinic Settings.
//
// PHA-1243: no dataset choice. A Reports-page report starts on Patients and moves to the
// billing dataset by itself when the request needs it; a Billing-page report (home 'billing',
// /billing/reports/new) is a billing report and returns to /billing.
//
// PHA-1244: each saved version keeps the prompts that built it. Edit shows them again, and
// "Edit prompt" puts them in the box to change and rebuild from, instead of starting blank.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge, Box, Button, Callout, Card, Checkbox, Dialog, Flex, Heading, IconButton,
  ScrollArea, Text, TextArea, TextField,
} from '@radix-ui/themes';
import {
  ExclamationTriangleIcon, InfoCircledIcon, MagnifyingGlassIcon, PaperPlaneIcon, Pencil1Icon, ResetIcon,
} from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import ClinicLayout from './ClinicLayout';
import ReportsLayout from '../reports/ReportsLayout';
import { invalidateSavedReports } from '../reports/useSavedReports';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  customViewErrorCode,
  customViewErrorDetails,
  customViewsApi,
  CustomViewField,
  CustomViewKind,
  CustomViewResult,
  CustomViewSpec,
  CustomViewSurface,
  ReportHome,
} from '../../services/api';
import ManifestTable from '../customViews/ManifestTable';
import ReportSummaryTable from '../customViews/ReportSummaryTable';
import { useCustomViews } from '../customViews/useCustomViews';
import { describeFilter } from '../customViews/describeSpec';

interface Message {
  role: 'user' | 'ai' | 'error';
  text: string;
  details?: string[];
  // A prompt of the saved version, shown when the builder opens to edit it.
  saved?: boolean;
}

const EXAMPLES: Record<CustomViewSurface, string[]> = {
  patient_list: [
    'Only APCM Tier 2 patients with no contact in 30 days. Show name, DOB, minutes this month and minutes left to 20. Sort by minutes left, most first. Red if systolic BP over 160.',
    'Onboarded patients with diabetes. Show last name, first name, conditions, last answer date and days since last contact.',
  ],
  billing: [
    'Patients who are not billable this month, with the reason. Show MRN, name, program, minutes and why not billable. Include patients with no activity.',
    'Group by program with subtotal minutes. Show MRN, name, CPT codes, RPM codes and minutes.',
  ],
};

// Report examples: the questions clinics ask most that the standard grids can't answer.
const REPORT_EXAMPLES: Record<CustomViewSurface, string[]> = {
  patient_list: [
    'Onboarded patients with no Voice of the Patient. Show MRN, name, care plans, VoP status.',
    'Patients whose current care plan is not uploaded to the EHR. Show MRN, name, push status, last push and error.',
    'Patients with a behavioral health care plan, grouped by clinic with a count.',
  ],
  billing: [
    'Patients who are not billable this month with the reason, for every enabled program. Show MRN, name, program, minutes and why not billable. Include patients with no activity.',
    'Group by program with subtotal minutes. Show MRN, name, CPT codes, RPM codes and minutes.',
  ],
};

// custom_views.ai.MAX_PROMPT_CHARS: the server keeps only this much of a prompt.
const MAX_PROMPT_CHARS = 2000;

const lastMonthRange = () => {
  const now = new Date();
  return {
    from: format(new Date(now.getFullYear(), now.getMonth() - 1, 1), 'yyyy-MM-dd'),
    to: format(new Date(now.getFullYear(), now.getMonth(), 0), 'yyyy-MM-dd'),
  };
};

// Page chrome: Clinic Settings for views, the Reports layout for reports.
const Frame: React.FC<{
  report: boolean; title: string; subtitle?: string; backTo?: string; children: React.ReactNode;
}> = ({ report, title, subtitle, backTo, children }) => (report
  ? <ReportsLayout title={title} subtitle={subtitle} backTo={backTo}>{children}</ReportsLayout>
  : <ClinicLayout title={title} subtitle={subtitle}>{children}</ClinicLayout>);

// Reports default to the month so far, like the Reports page.
const currentMonthRange = () => {
  const now = new Date();
  return { from: format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'), to: format(now, 'yyyy-MM-dd') };
};

const ViewBuilder: React.FC<{ kind?: CustomViewKind; home?: ReportHome }> = ({ kind = 'view', home = 'reports' }) => {
  const navigate = useNavigate();
  // Views live at /clinic-settings/views/:viewId, reports at /reports/:id/edit (or /reports/new).
  const { viewId, id } = useParams<{ viewId?: string; id?: string }>();
  const viewIdParam = viewId ?? id;
  const [params] = useSearchParams();
  const { orgId, orgName } = useOrganization();
  const isReport = kind === 'report';
  // Sent only for reports, so the views flow's requests are exactly what they were.
  const kindArg: [CustomViewKind?] = isReport ? ['report'] : [];
  const editingId = viewIdParam && viewIdParam !== 'new' ? Number(viewIdParam) : null;

  const datasetParam = isReport ? params.get('dataset') ?? params.get('surface') : params.get('surface');
  // The page a report lives on; an existing report's own home replaces it once loaded.
  const [reportHome, setReportHome] = useState<ReportHome>(home);
  const [surface, setSurface] = useState<CustomViewSurface>(
    datasetParam === 'billing' || (isReport && home === 'billing') ? 'billing' : 'patient_list');
  const access = useCustomViews(orgId, surface, kind);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [spec, setSpec] = useState<CustomViewSpec | null>(null);
  const [canEdit, setCanEdit] = useState(true);
  const [wasDefault, setWasDefault] = useState(false);
  const [fields, setFields] = useState<CustomViewField[]>([]);
  const [fieldQuery, setFieldQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  // The prompts that produced `spec`, oldest first: the saved ones, then this session's
  // successful turns. Saved with the version.
  const [turns, setTurns] = useState<string[]>([]);
  const [savedPromptsLoaded, setSavedPromptsLoaded] = useState(false);
  // The first turn built `spec` from nothing, so the turns alone can rebuild it.
  const [fresh, setFresh] = useState(false);
  // The box holds the prompts to edit: 'rebuild' sends them with no prior definition,
  // 'change' as a change on top of `spec`.
  const [promptEdit, setPromptEdit] = useState<'rebuild' | 'change' | null>(null);
  const [prompt, setPrompt] = useState('');
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState<CustomViewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [range, setRange] = useState(isReport ? currentMonthRange : lastMonthRange);
  const [saveOpen, setSaveOpen] = useState(false);
  const [makeDefault, setMakeDefault] = useState(false);
  const [share, setShare] = useState(false);
  const [confirmHidden, setConfirmHidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const threadEnd = useRef<HTMLDivElement>(null);

  const fieldByKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);

  // Load the field catalog (and the saved view, when editing).
  useEffect(() => {
    if (!orgId || (orgId === 'ALL' && !isReport)) return;
    let cancelled = false;
    (async () => {
      try {
        let s = surface;
        if (editingId) {
          const def = await customViewsApi.get(orgId, editingId, isReport ? 'report' : undefined, { includePrompts: true });
          if (cancelled) return;
          s = def.surface;
          setSurface(def.surface);
          setName(def.name);
          setDescription(def.description || '');
          setSpec(def.spec);
          setCanEdit(def.can_edit);
          setWasDefault(def.is_default);
          setMakeDefault(def.is_default);
          if (isReport) setReportHome(def.home ?? 'reports');
          const saved = def.prompts ?? [];
          setTurns(saved);
          setFresh(!!def.prompts_complete);
          setMessages(saved.map((text) => ({ role: 'user' as const, text, saved: true })));
          setSavedPromptsLoaded(true);
        }
        const cat = await customViewsApi.catalog(orgId, s, ...kindArg);
        if (!cancelled) setFields(cat.fields);
      } catch (err) {
        if (!cancelled) toast.error(customViewErrorDetails(err).join(' '));
      }
    })();
    return () => { cancelled = true; };
    // surface changes only via the saved view here; a new view's surface is fixed by the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, editingId]);

  const runPreview = useCallback(async (next: CustomViewSpec) => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const data = await customViewsApi.preview(orgId, surface, next,
        surface === 'billing' ? { from_date: range.from, to_date: range.to } : {}, ...kindArg);
      setPreview(data);
    } catch (err) {
      setPreviewError(customViewErrorDetails(err).join(' '));
    } finally {
      setPreviewing(false);
    }
  }, [orgId, surface, range, isReport]); // eslint-disable-line react-hooks/exhaustive-deps

  // Editing: preview the saved definition once it (and the catalog) has loaded.
  // A new view previews from send() instead, so this must not fire for it.
  const previewedOnce = useRef(false);
  useEffect(() => {
    if (editingId && spec && fields.length && !previewedOnce.current) {
      previewedOnce.current = true;
      void runPreview(spec);
    }
  }, [editingId, spec, fields.length, runPreview]);

  useEffect(() => { threadEnd.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  const send = async (text?: string) => {
    const message = (text ?? prompt).trim();
    if (!message || sending || message.length > MAX_PROMPT_CHARS) return;
    setMessages((m) => [...m, { role: 'user', text: message }]);
    setPrompt('');
    setSending(true);
    const prior = promptEdit === 'rebuild' ? null : spec;
    setPromptEdit(null);
    try {
      const res = await customViewsApi.design(orgId, surface, message, prior, editingId, ...kindArg);
      if (isReport && !editingId && res.switch_dataset && res.switch_dataset !== surface) {
        // Restart the builder on that dataset with the same request (NewReportRoute
        // remounts on ?dataset), which sends it again there.
        const next = new URLSearchParams({ dataset: res.switch_dataset, prompt: message, switched: '1' });
        navigate(`/reports/new?${next.toString()}`, { replace: true });
        return;
      }
      if (res.ok && res.spec) {
        setSpec(res.spec);
        const reply: Message = { role: 'ai', text: res.reply || 'Here is your view.' };
        if (prior) {
          setTurns((t) => [...t, message]);
          setMessages((m) => [...m, reply]);
        } else {
          // Built from nothing: this prompt alone is the thread now, and it is complete.
          setTurns([message]);
          setFresh(true);
          setMessages((m) => (m.some((x) => x.saved) ? [{ role: 'user', text: message }, reply] : [...m, reply]));
        }
        await runPreview(res.spec);
      } else {
        setMessages((m) => [...m, {
          role: 'error',
          text: "I couldn't build that view. The last working preview is unchanged.",
          details: res.errors,
        }]);
      }
    } catch (err) {
      setMessages((m) => [...m, { role: 'error', text: customViewErrorDetails(err).join(' ') }]);
    } finally {
      setSending(false);
    }
  };

  // A report restarted on this dataset (see send) carries the admin's request:
  // send it once the catalog is in, then drop it from the URL so a reload doesn't resend.
  const carriedPrompt = isReport && !editingId ? params.get('prompt') : null;
  const sentCarried = useRef(false);
  useEffect(() => {
    if (!carriedPrompt || !fields.length || sentCarried.current) return;
    sentCarried.current = true;
    navigate(`/reports/new?dataset=${surface}`, { replace: true });
    void send(carriedPrompt);
  }, [carriedPrompt, fields.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // The prompts as one prompt to edit. When they start from nothing, sending rebuilds the
  // definition from the edited prompt alone; otherwise the earliest ones are missing, so a
  // rebuild would lose what they did, and it is sent as a change on top of the current one.
  const editSavedPrompt = () => {
    setPrompt(turns.join('\n\n'));
    setPromptEdit(fresh ? 'rebuild' : 'change');
    promptRef.current?.focus();
  };

  const insertField = (f: CustomViewField) => {
    setPrompt((p) => (p && !p.endsWith(' ') ? `${p} ${f.label}` : `${p}${f.label}`));
    promptRef.current?.focus();
  };

  const filteredFields = useMemo(() => {
    const q = fieldQuery.trim().toLowerCase();
    const list = q
      ? fields.filter((f) => f.label.toLowerCase().includes(q) || f.key.includes(q) || f.description.toLowerCase().includes(q))
      : fields;
    const groups = new Map<string, CustomViewField[]>();
    list.forEach((f) => groups.set(f.group, [...(groups.get(f.group) || []), f]));
    return Array.from(groups.entries());
  }, [fields, fieldQuery]);

  // A report filtering rows is the point of a report, so only views need the confirmation.
  // The server cuts a longer prompt, which would drop its last (newest) instructions.
  const promptTooLong = prompt.trim().length > MAX_PROMPT_CHARS;
  const billingFilters = !isReport && surface === 'billing' && (spec?.filters?.length || 0) > 0;
  const hiddenRows = preview?.hidden_billable_rows || 0;

  const save = async () => {
    if (!spec) return;
    setSaving(true);
    setSaveErrors([]);
    // A network's view opened from a child clinic is saved as a new view here.
    const updatingId = canEdit ? editingId : null;
    try {
      const saved = await customViewsApi.save(orgId, {
        surface, name: name.trim(), description: description.trim() || undefined, spec,
        view_id: updatingId,
        prompts: turns,
        prompts_complete: fresh,
        ...(isReport
          ? { kind: 'report' as const, home: reportHome, share_with_child_clinics: !updatingId && share && orgId !== 'ALL' }
          : {
            make_default: makeDefault, share_with_child_clinics: !updatingId && share,
            confirm_hidden_rows: billingFilters ? confirmHidden : undefined,
          }),
      });
      if (!isReport && updatingId && wasDefault && !makeDefault) {
        // Unchecking "default" on the current default returns the clinic to Phanova Standard.
        await customViewsApi.setDefault(orgId, surface, null);
      }
      toast.success(`"${name.trim()}" saved`);
      access.refresh();
      if (isReport) {
        invalidateSavedReports();
        navigate(`/reports/${saved.view_id}`);
      } else {
        navigate(`/clinic-settings/views?surface=${surface}`);
      }
    } catch (err) {
      if (customViewErrorCode(err) === 'confirm_hidden_rows') setConfirmHidden(false);
      setSaveErrors(customViewErrorDetails(err));
    } finally {
      setSaving(false);
    }
  };

  const noun = isReport ? 'report' : 'view';
  if (!orgId || (orgId === 'ALL' && !isReport)) {
    return (
      <Frame report={isReport} title={isReport ? 'Report builder' : 'View builder'}>
        <Callout.Root><Callout.Icon><InfoCircledIcon /></Callout.Icon>
          <Callout.Text>Pick a clinic to design its {noun}s.</Callout.Text></Callout.Root>
      </Frame>
    );
  }
  if (!access.loading && (!access.enabled || !access.canDesign)) {
    return (
      <Frame report={isReport} title={isReport ? 'Report builder' : 'View builder'}>
        <Callout.Root color="orange"><Callout.Icon><InfoCircledIcon /></Callout.Icon>
          <Callout.Text>
            {access.enabled
              ? `Only clinic admins can design ${noun}s.`
              : `${isReport ? 'Reports are' : 'Custom views are'} not turned on for this clinic.`}
          </Callout.Text></Callout.Root>
      </Frame>
    );
  }

  const surfaceLabel = surface === 'billing' ? 'Billing Report' : 'Patient List';
  const phanovaWide = isReport && orgId === 'ALL';
  const reportsHomePath = reportHome === 'billing' ? '/billing' : '/reports';
  const leave = () => navigate(isReport ? (editingId ? `/reports/${editingId}` : reportsHomePath) : `/clinic-settings/views?surface=${surface}`);
  const reportSubtitle = phanovaWide
    ? 'Phanova-wide report. Visible to Phanova staff only, runs across all clinics. Draft until you save.'
    : canEdit
      ? 'Draft · not visible to your team until you save.'
      : 'This report belongs to your network. You can try changes here, but save it as a new report for this clinic.';

  return (
    <Frame
      report={isReport}
      backTo={isReport ? reportsHomePath : undefined}
      title={isReport
        ? (editingId ? `Edit report${name ? `: ${name}` : ''}` : reportHome === 'billing' ? 'New billing report' : 'New report')
        : (editingId ? `Edit view${name ? `: ${name}` : ''}` : `New ${surfaceLabel} view`)}
      subtitle={isReport ? reportSubtitle : (canEdit
        ? 'Draft · not visible to your team until you save.'
        : 'This view belongs to your network. You can try changes here, but save it as a new view for this clinic.')}
    >
      {phanovaWide && (
        <Flex gap="2" align="center" mb="3" wrap="wrap">
          <Badge color="purple" data-testid="phanova-wide-badge">
            Phanova-wide report — visible to Phanova staff only, runs across all clinics
          </Badge>
        </Flex>
      )}
      <Flex justify="end" gap="2" mb="3">
        <Button variant="soft" color="gray" onClick={leave}>
          Cancel
        </Button>
        <Button variant="soft" disabled={!spec || sending}
          onClick={() => { setSpec(null); setPreview(null); setMessages([]); setTurns([]); setPromptEdit(null); }}>
          <ResetIcon /> Start over
        </Button>
        <Button disabled={!spec || sending || previewing || !!previewError} onClick={() => {
          setSaveErrors([]);
          setConfirmHidden(false);
          if (!canEdit && editingId) setName(`${name} (copy)`);
          setSaveOpen(true);
        }}>
          {isReport ? 'Save report…' : 'Save view…'}
        </Button>
      </Flex>

      <Flex gap="4" align="start" direction={{ initial: 'column', md: 'row' }}>
        {/* Prompt thread */}
        <Card style={{ flex: '1 1 340px', minWidth: 300 }}>
          <Flex direction="column" gap="3">
            <Box>
              <Heading size="3">Describe your {noun}</Heading>
              <Text size="1" color="gray">AI turns your words into a {noun} definition. It never sees patient data.</Text>
            </Box>
            <ScrollArea style={{ maxHeight: 360 }} scrollbars="vertical">
              <Flex direction="column" gap="2" pr="2">
                {messages.length === 0 && editingId && savedPromptsLoaded && spec && (
                  <Text size="1" color="gray" data-testid="no-saved-prompt">
                    This {noun} was saved without its prompt. Describe a change, or start over with a new prompt.
                  </Text>
                )}
                {messages.length === 0 && (!editingId || !spec) && (
                  <Flex direction="column" gap="2">
                    <Text size="1" color="gray">Try one of these:</Text>
                    {(isReport ? REPORT_EXAMPLES : EXAMPLES)[surface].map((ex) => (
                      <Button key={ex} size="1" variant="soft" color="gray"
                        style={{ height: 'auto', whiteSpace: 'normal', textAlign: 'left', padding: 8 }}
                        onClick={() => setPrompt(ex)}>
                        {ex}
                      </Button>
                    ))}
                  </Flex>
                )}
                {messages.some((m) => m.saved) && (
                  <Text size="1" color="gray" weight="medium">
                    {messages.filter((m) => m.saved).length > 1 ? 'Prompts' : 'Prompt'} that built this {noun}
                  </Text>
                )}
                {messages.map((m, i) => (
                  <Box key={i} p="2" data-testid={m.saved ? 'saved-prompt' : undefined} style={{
                    borderRadius: 8,
                    background: m.role === 'user' ? '#eef4ff' : m.role === 'error' ? '#fdecec' : '#f4f5f7',
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '92%',
                  }}>
                    <Text size="2">{m.text}</Text>
                    {m.details && m.details.length > 0 && (
                      <ul style={{ margin: '4px 0 0 16px' }}>
                        {m.details.slice(0, 6).map((d, j) => <li key={j}><Text size="1">{d}</Text></li>)}
                      </ul>
                    )}
                  </Box>
                ))}
                {turns.length > 0 && !promptEdit && !sending && (
                  <Flex justify="end">
                    <Button size="1" variant="ghost" onClick={editSavedPrompt}>
                      <Pencil1Icon /> Edit prompt
                    </Button>
                  </Flex>
                )}
                {sending && <Text size="1" color="gray">Designing…</Text>}
                <div ref={threadEnd} />
              </Flex>
            </ScrollArea>
            <Flex gap="2" align="end">
              <TextArea
                ref={promptRef}
                placeholder={spec ? 'Ask for a change, e.g. "also show RPM days"' : `Describe the ${noun} you want…`}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
                }}
                rows={3}
                style={{ flex: 1 }}
                aria-label={`Describe your ${noun}`}
              />
              <IconButton size="3" onClick={() => void send()} aria-label="Send"
                disabled={!prompt.trim() || sending || promptTooLong}>
                <PaperPlaneIcon />
              </IconButton>
            </Flex>
            {promptTooLong && (
              <Text size="1" color="red" data-testid="prompt-too-long">
                {prompt.trim().length} of {MAX_PROMPT_CHARS} characters. Shorten it before sending.
              </Text>
            )}
            {promptEdit ? (
              <Flex gap="2" align="center" wrap="wrap">
                <Text size="1" color="blue">
                  {promptEdit === 'rebuild'
                    ? `Sending builds the ${noun} again from this prompt alone, replacing the current definition.`
                    : `The first prompts of this ${noun} weren't kept, so this is sent as a change to the current definition.`}
                </Text>
                <Button size="1" variant="ghost" color="gray" onClick={() => { setPromptEdit(null); setPrompt(''); }}>
                  Cancel edit
                </Button>
              </Flex>
            ) : (
              <Text size="1" color="gray">Tip: click a field on the right to add it to your prompt.</Text>
            )}
          </Flex>
        </Card>

        {/* Field panel */}
        <Card style={{ flex: '0 1 280px', minWidth: 240 }}>
          <Flex direction="column" gap="2">
            <Flex justify="between" align="center">
              <Heading size="3">Available fields</Heading>
              <Text size="1" color="gray">{fields.length} fields</Text>
            </Flex>
            <TextField.Root size="1">
              <TextField.Slot><MagnifyingGlassIcon /></TextField.Slot>
              <TextField.Input placeholder="Search fields" value={fieldQuery}
                onChange={(e) => setFieldQuery(e.target.value)} aria-label="Search fields" />
            </TextField.Root>
            <ScrollArea style={{ maxHeight: 420 }} scrollbars="vertical">
              <Flex direction="column" gap="3" pr="2">
                {filteredFields.map(([group, list]) => (
                  <Box key={group}>
                    <Text size="1" weight="bold" color="gray">{group}</Text>
                    <Flex direction="column" gap="1" mt="1">
                      {list.map((f) => (
                        <button key={f.key} type="button" onClick={() => insertField(f)}
                          title={f.description || f.label}
                          className="text-left rounded px-2 py-1 hover:bg-slate-100"
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
                          <Flex justify="between" align="center" gap="2">
                            <Text size="2">{f.label}</Text>
                            <Flex gap="1">
                              {f.identity
                                ? <Badge size="1" color="gray">column only</Badge>
                                : !f.filterable && <Badge size="1" color="gray">display</Badge>}
                              {f.locked && <Badge size="1" color="amber">code</Badge>}
                            </Flex>
                          </Flex>
                        </button>
                      ))}
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </ScrollArea>
          </Flex>
        </Card>
      </Flex>

      {/* Preview */}
      <Card mt="4">
        <Flex direction="column" gap="3">
          <Flex justify="between" align="center" wrap="wrap" gap="2">
            <Box>
              <Heading size="3">Preview</Heading>
              <Text size="1" color="gray">
                {preview
                  ? `First ${Math.min(preview.records.length, 50)} of ${preview.total_count} rows · real ${orgName || 'clinic'} data`
                  : 'Describe a view to see it here.'}
              </Text>
            </Box>
            {surface === 'billing' && (
              <Flex gap="2" align="center">
                <Text size="1" color="gray">Dates</Text>
                <input type="date" value={range.from} aria-label="Preview from date"
                  onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
                <input type="date" value={range.to} aria-label="Preview to date"
                  onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
                <Button size="1" variant="soft" disabled={!spec || previewing} onClick={() => spec && runPreview(spec)}>
                  Refresh
                </Button>
              </Flex>
            )}
          </Flex>

          {spec && (spec.filters?.length || 0) > 0 && (
            <Flex gap="1" wrap="wrap" align="center">
              <Text size="1" color="gray">Filters:</Text>
              {spec.filters!.map((f, i) => (
                <Badge key={i} variant="soft" color="blue">{describeFilter(f, fieldByKey)}</Badge>
              ))}
            </Flex>
          )}
          {!isReport && surface === 'billing' && hiddenRows > 0 && (
            <Callout.Root color="amber">
              <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
              <Callout.Text>
                This view hides {hiddenRows} billable {hiddenRows === 1 ? 'row' : 'rows'} of {preview?.billable_total}.
                Saving it will need your confirmation.
              </Callout.Text>
            </Callout.Root>
          )}
          {previewError && (
            <Callout.Root color="red">
              <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
              <Callout.Text>{previewError}</Callout.Text>
            </Callout.Root>
          )}
          {previewing && <Text size="1" color="gray">Running preview…</Text>}
          {preview && (
            <Box style={{ overflowX: 'auto', opacity: previewing ? 0.6 : 1 }}>
              {preview.summary && (
                <Box mb="3"><ReportSummaryTable summary={preview.summary} /></Box>
              )}
              <ManifestTable manifest={preview.manifest} records={preview.records}
                groups={preview.groups} totals={preview.totals} dense />
            </Box>
          )}
        </Flex>
      </Card>

      <Dialog.Root open={saveOpen} onOpenChange={setSaveOpen}>
        <Dialog.Content style={{ maxWidth: 480 }}>
          <Dialog.Title>{isReport ? 'Save report' : 'Save view'}</Dialog.Title>
          <Dialog.Description size="2" color="gray" mb="3">
            {isReport
              ? `Name it so your team can find it on the ${reportHome === 'billing' ? 'Billing' : 'Reports'} page. The name becomes the #handle you type in Ask Phanova.`
              : `Name it so your team can pick it on the ${surfaceLabel}.`}
          </Dialog.Description>
          <Flex direction="column" gap="3">
            <label>
              <Text size="2" weight="medium">{isReport ? 'Report name' : 'View name'}</Text>
              <TextField.Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
                placeholder={isReport
                  ? (surface === 'billing' ? 'e.g. Not billable this month' : 'e.g. Missing Voice of the Patient')
                  : (surface === 'billing' ? 'e.g. Not billable this month' : 'e.g. Tier 2 outreach')} />
            </label>
            <label>
              <Text size="2" weight="medium">Description (optional)</Text>
              <TextField.Input value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
            </label>
            {!isReport && (
              <Text as="label" size="2">
                <Flex gap="2" align="start">
                  <Checkbox checked={makeDefault} onCheckedChange={(c) => setMakeDefault(c === true)} />
                  <span>
                    Make this the clinic default
                    <Text as="div" size="1" color="gray">
                      Everyone at {orgName || 'this clinic'} sees it first on the {surfaceLabel}. They can still switch views.
                    </Text>
                  </span>
                </Flex>
              </Text>
            )}
            {(!editingId || !canEdit) && access.canShare && !phanovaWide && (
              <Text as="label" size="2">
                <Flex gap="2" align="start">
                  <Checkbox checked={share} onCheckedChange={(c) => setShare(c === true)} />
                  <span>
                    Share with all child clinics
                    <Text as="div" size="1" color="gray">{`Saves the ${noun} on your network so every clinic under it can use it.`}</Text>
                  </span>
                </Flex>
              </Text>
            )}
            {billingFilters && (
              <Box p="2" style={{ background: '#fff8e6', border: '1px solid #f0d9a0', borderRadius: 6 }}>
                <Text as="label" size="2">
                  <Flex gap="2" align="start">
                    <Checkbox checked={confirmHidden} onCheckedChange={(c) => setConfirmHidden(c === true)} />
                    <span>
                      I understand this view filters rows and can hide billable patients
                      {hiddenRows > 0 ? ` (${hiddenRows} in the preview)` : ''}.
                    </span>
                  </Flex>
                </Text>
              </Box>
            )}
            <Text size="1" color="gray">
              {isReport
                ? 'Saving turns this report into a fixed, fast query. Running it, here or from Ask Phanova, never calls the AI to build it again.'
                : `Saving turns this view into a fixed, fast query. Opening the ${surfaceLabel} never calls the AI again.`}
            </Text>
            {phanovaWide && (
              <Text size="1" color="gray">This report is visible to Phanova staff only and runs across all clinics.</Text>
            )}
            {!isReport && canEdit && wasDefault && !makeDefault && (
              <Text size="1" color="orange">This view is the clinic default now. Saving with this unchecked makes Phanova Standard the default again.</Text>
            )}
            {saveErrors.length > 0 && (
              <Callout.Root color="red">
                <Callout.Text>{saveErrors.join(' ')}</Callout.Text>
              </Callout.Root>
            )}
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close><Button variant="soft" color="gray">Cancel</Button></Dialog.Close>
            <Button onClick={() => void save()}
              disabled={saving || !name.trim() || (billingFilters && !confirmHidden)}>
              {saving ? 'Saving…' : isReport ? 'Save report' : 'Save view'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Frame>
  );
};

export default ViewBuilder;
